import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { fetchFromNewApi } from '~/services/Http';

import { getLanguages } from '../languages';

import { CustomTranslations, CustomTranslationsIncoming } from './types';

import { newToOldTranslationKeys } from '.';

export type StoreState = {
  customTranslations: CustomTranslations;
  hasData: boolean;
  isLoading: boolean;
  failedToLoad: boolean;
};

const initialState: StoreState = {
  customTranslations: {},
  hasData: false,
  isLoading: false,
  failedToLoad: false,
};

export enum CustomTranslationsActionKeyPrefixes {
  FETCH_CUSTOM_TRANSLATIONS = 'customTranslations/FETCH_CUSTOM_TRANSLATIONS',
}

export type CustomTrasnlationsPayload = {
  customTranslations: CustomTranslations;
};

type LanguageTerms = {
  terms: string[];
  locale: string;
};

const getLanguageTranslations = async (
  languageTerms: LanguageTerms,
): Promise<CustomTranslationsIncoming> =>
  fetchFromNewApi(`/i18n/translations/terms/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(languageTerms),
  }).then((r) => r.json());

export const fetchCustomTranslations = createAsyncThunk<
  CustomTrasnlationsPayload,
  undefined
>(
  CustomTranslationsActionKeyPrefixes.FETCH_CUSTOM_TRANSLATIONS,
  async (): Promise<CustomTrasnlationsPayload> => {
    return getLanguages()
      .then((companyLanguages) =>
        Promise.all(
          companyLanguages.map((lang) => {
            const languageTerms = {
              terms: Object.keys(newToOldTranslationKeys),
              locale: lang.fComLanguageCode,
            };

            return getLanguageTranslations(languageTerms).then(
              (trans): CustomTranslations => ({
                [lang.fComLanguageCode]: trans.data.translations,
              }),
            );
          }),
        ),
      )
      .then((languageSpecificTranslations): CustomTrasnlationsPayload => {
        return {
          customTranslations: languageSpecificTranslations.reduce(
            (acc, curr): CustomTranslations => ({ ...acc, ...curr }),
            {},
          ),
        };
      });
  },
);

export const customTranslationsSlice = createSlice({
  name: '~/data/customTranslations',
  initialState,
  reducers: {},
  extraReducers: {
    [fetchCustomTranslations.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<CustomTrasnlationsPayload>,
    ) => {
      state.failedToLoad = false;
      state.isLoading = false;
      state.hasData = true;
      state.customTranslations = action.payload.customTranslations;
    },
    [fetchCustomTranslations.pending.type]: (state: StoreState) => {
      state.isLoading = true;
    },
    [fetchCustomTranslations.rejected.type]: (state: StoreState) => {
      state.failedToLoad = true;
    },
  },
});

export const customTranslationsReducer = customTranslationsSlice.reducer;
